import { useCallback } from 'react';
import { setPersonalizationData, useAppContext } from '../../../context/app-context';
import { useCardContext } from '../../../context/card-context';
import { useInitializationDataContext } from '../../../context/data-context';
import { useLoadCardData, useSystemErrorHandling } from '../../../hooks';
import { useIsOneToMany } from '../../../hooks/use-is-one-to-many';
import { getTransformedPersonalizationData, isArrayPopulated } from '../../../utils';

type LoadCardDataCallbacks = {
  loadCardDataAfterLogin: () => void;
};
/**
 * Handles the update of cardFacesList state from card-context and personalizationData state for app-context
 * After login the contexts are re-initialized with the refresh of the page so we need to populate this states after getting the data from the loaded project
 */
export const useLoadCardAfterLogin = (): LoadCardDataCallbacks => {
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const { appState, appDispatch } = useAppContext();
  const { cardState } = useCardContext();
  const { loadCardData } = useLoadCardData();
  const isOneToMany = useIsOneToMany();
  const { onSystemError } = useSystemErrorHandling();

  const loadCardDataAfterLogin = useCallback(async () => {
    const projectTypeCode = initializedData?.project_type_code;
    if (!projectTypeCode || appState.personalizationData.length > 0 || !isArrayPopulated(cardState.cardFacesList)) {
      return;
    }
    getTransformedPersonalizationData(cardState, projectTypeCode, isOneToMany)
      .then((personalizationData) => {
        return setPersonalizationData(appDispatch, personalizationData);
      })
      .catch((error) => {
        onSystemError(
          {
            status: 0,
            data: {
              errors: [
                {
                  code: '0',
                  description: error,
                },
              ],
            },
          },
          {
            errorCallback: () => {
              throw new Error(error);
            },
          },
        );
      });
    loadCardData();
  }, [cardState.cardFacesList, loadCardData, initializedData?.project_type_code]);

  return { loadCardDataAfterLogin };
};
